import type { DocTypeSchema } from "@/lib/erp/meta";
import type { ChildTableRow } from "../types";
import { isEffectivelyEmptyRow } from "./validation";
import { getVisibleChildTableFields } from "./schemaUtils";

/**
 * Remove effectively-empty local placeholder rows from all child tables on a document.
 *
 * Why this exists:
 * - Many UIs render pre-created placeholder child rows (e.g. 5 gallery rows).
 * - Frappe enforces mandatory fields on child rows too.
 * - We must drop placeholder __islocal rows before sending docs to Frappe.
 *
 * Notes:
 * - Only prunes rows with `__islocal` to avoid dropping real server rows.
 * - Requires child schemas to compute visible fields; if a child schema is unavailable, we skip pruning that table.
 */
export function pruneLocalPlaceholderRows(
  doc: Record<string, any>,
  parentSchema: DocTypeSchema,
  getChildSchema: (doctype: string) => DocTypeSchema | undefined
): Record<string, any> {
  const tableFields = (parentSchema.fields || []).filter((f) => f.fieldtype === "Table" && f.options);
  if (tableFields.length === 0) return doc;

  let changed = false;
  const next = { ...doc };

  for (const field of tableFields) {
    const rows = next[field.fieldname];
    if (!Array.isArray(rows) || rows.length === 0) continue;

    const childSchema = getChildSchema(String(field.options));
    if (!childSchema?.fields?.length) continue;

    const visibleFields = getVisibleChildTableFields(childSchema);
    if (visibleFields.length === 0) continue;

    const beforeLen = rows.length;
    const pruned = (rows as ChildTableRow[]).filter((row) => {
      if ((row as any).__islocal && isEffectivelyEmptyRow(row, visibleFields)) {
        return false;
      }
      return true;
    });

    if (pruned.length !== beforeLen) {
      next[field.fieldname] = pruned;
      changed = true;
    }
  }

  return changed ? next : doc;
}

